import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Shield,
  Lock,
  Cloud,
  Cpu,
  Binary,
  Fingerprint,
  Radio
} from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, type: 'spring', stiffness: 100 } }
  };

  // Subtle tech grid
  const TechGrid = () => (
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full" />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full" />
        ))}
      </div>
    </div>
  );

  // Soft scanline
  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32 pointer-events-none"
      animate={{ top: ['-10%', '110%'] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  );

  const capabilities = [
    {
      icon: Cpu,
      title: "Neuromorphic Computing",
      description: "Research into brain-inspired architectures for cybersecurity applications"
    },
    {
      icon: Lock,
      title: "Post-Quantum Cryptography",
      description: "Research into cryptographic schemes designed to resist future quantum-computing attacks"
    }
  ];

  const principles = [
    { icon: Lock, text: "Security considered from the start of every project" },
    { icon: Binary, text: "Careful, disciplined engineering practices" },
    { icon: Fingerprint, text: "Access control and least-privilege principles" },
    { icon: Radio, text: "Reliable systems built for demanding conditions" }
  ];

  return (
    <div className="relative">
      {/* Fixed background layer that spans all sections */}
      <div className="fixed inset-0 z-0 bg-black">
        <ParticleBackground />
      </div>

      {/* Content wrapper with proper z-index */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
          <TechGrid />
          <ScanLine />

          {/* Decorative rails */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent z-[5]" />
          <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent z-[5]" />

          <div className="container mx-auto px-4 z-20 relative py-24">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Logo */}
              <motion.div variants={itemVariants} className="mb-8">
                <img
                  src="/images/reticuli-logo.png"
                  alt="Reticuli Technologies"
                  className="h-[20rem] md:h-[24rem] mx-auto"
                />
              </motion.div>

              {/* Company Description */}
              <motion.p
                variants={itemVariants}
                className="text-xl md:text-2xl font-light text-gray-300 mb-6 leading-relaxed max-w-3xl mx-auto"
              >
                <span className="text-white font-medium">Reticuli Technologies</span> is an early-stage
                R&D company researching neuromorphic computing and post-quantum cryptography.
              </motion.p>

              <motion.div variants={itemVariants} className="mb-10 flex justify-center">
                <span className="inline-flex items-center px-3 py-1 rounded-full border border-gray-700/50 bg-gray-900/50 text-xs tracking-wide text-gray-500 uppercase">
                  Early-stage R&D company
                </span>
              </motion.div>

              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium rounded-md transition-all duration-300 flex items-center justify-center group shadow-lg shadow-black/30 border border-gray-600/30"
                >
                  Contact Us
                  <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                </Link>
                <a
                  href="#capabilities"
                  className="px-8 py-4 border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white font-medium rounded-md transition-all duration-300 flex items-center justify-center"
                >
                  Our Focus Areas
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="relative py-24 bg-gradient-to-b from-black/90 via-gray-950/95 to-gray-950/95 overflow-hidden">
          {/* Subtle accent glow */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(156,163,175,0.06)_0%,_transparent_50%)] pointer-events-none" />
          
          {/* Decorative line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-500/30 to-transparent" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {/* Section header */}
              <div className="text-center mb-16">
                <motion.div 
                  className="inline-flex items-center justify-center w-20 h-20 mb-6 relative"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="absolute inset-0 border-2 border-gray-600/40 rotate-45" />
                  <div className="absolute inset-2 border border-gray-700/30 rotate-45" />
                  <Shield className="w-8 h-8 text-gray-300" strokeWidth={1.5} />
                </motion.div>
                
                <motion.h2 
                  className="text-4xl md:text-5xl font-extralight tracking-[0.15em] text-white mb-4"
                  style={{ fontFamily: 'Georgia, Cambria, serif' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  OUR MISSION
                </motion.h2>

                <motion.div 
                  className="flex items-center justify-center gap-4 mb-8"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="w-16 h-px bg-gradient-to-r from-transparent to-gray-600" />
                  <div className="w-2 h-2 rotate-45 border border-gray-600" />
                  <div className="w-16 h-px bg-gradient-to-l from-transparent to-gray-600" />
                </motion.div>

                <motion.p
                  className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                >
                  We're an early-stage R&D company focused on two areas: neuromorphic computing
                  for cybersecurity applications, and post-quantum cryptography research.
                </motion.p>

                <motion.p
                  className="text-gray-500 max-w-2xl mx-auto leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                >
                  Our work is still early. We're building carefully and sharing what we learn
                  as our research matures.
                </motion.p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section id="capabilities" className="relative py-20 bg-gray-950/95">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3
                  className="text-3xl font-extralight text-white mb-4 tracking-wider"
                  style={{ fontFamily: 'Georgia, Cambria, serif' }}
                >
                  Focus Areas
                </h3>
                <p className="text-gray-500 max-w-2xl mx-auto">
                  The two research areas we're focused on.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {capabilities.map((capability, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group p-8 bg-gray-900/50 backdrop-blur-sm border border-gray-800/50 rounded-lg hover:border-gray-700/50 transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-lg bg-gray-800/50 flex items-center justify-center mb-6 group-hover:bg-gray-800 transition-colors">
                      <capability.icon className="w-7 h-7 text-gray-400 group-hover:text-gray-300 transition-colors" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-xl font-medium text-white mb-3">{capability.title}</h4>
                    <p className="text-gray-500 leading-relaxed">{capability.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Principles Section */}
        <section className="relative py-20 bg-gradient-to-b from-gray-950/95 to-black/90 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-px h-3/4 bg-gradient-to-b from-transparent via-gray-700/30 to-transparent" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-3/4 bg-gradient-to-b from-transparent via-gray-700/30 to-transparent" />

          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 
                  className="text-3xl font-extralight text-white mb-4 tracking-wider"
                  style={{ fontFamily: 'Georgia, Cambria, serif' }}
                >
                  Core Principles
                </h3>
              </motion.div>

              <div className="space-y-6">
                {principles.map((principle, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    className={`flex items-center gap-6 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}
                  >
                    <div className="flex-1 p-6 bg-gray-900/50 backdrop-blur-sm border border-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gray-800/50 flex items-center justify-center">
                          <principle.icon className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                        </div>
                        <p className="text-gray-300">{principle.text}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-12 h-12 items-center justify-center">
                      <div className="w-3 h-3 rotate-45 border border-gray-700" />
                    </div>
                    <div className="hidden md:block flex-1" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 bg-gradient-to-b from-black/90 to-gray-950/95 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(156,163,175,0.04)_0%,_transparent_60%)] pointer-events-none" />
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 mb-8 rounded-full border border-gray-700/50 bg-gray-900/50 backdrop-blur-sm">
                <Cloud className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
              </div>
              
              <h3 
                className="text-3xl md:text-4xl font-extralight text-white mb-6 tracking-wider"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                Let's Build Something That Matters
              </h3>
              
              <p className="text-gray-500 mb-10 max-w-xl mx-auto">
                We work with partners who share our commitment to excellence and understand 
                that some problems require unconventional solutions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-10 py-4 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium tracking-wider rounded-md transition-all duration-300 flex items-center justify-center group shadow-lg shadow-black/30 border border-gray-600/30"
                >
                  Start a Conversation
                  <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Bottom decorative line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-700/20 to-transparent" />
        </section>
      </div>
    </div>
  );
};

export default About;